<template>
    <div>
        <span class="text-caption">
            {{ label }}
        </span>
        <div class="custom-textarea my-2" ref="editable" contenteditable @input="onInput" @copy="handleCopy" @paste="handlePaste" @cut="handleCut"></div>
    </div>
</template>

<script>
    export default {
        props: {
            label: String,
            value: String,
        },

        mounted() {
            this.setContent(this.value)
            setTimeout(() => {
                this.focus()
            }, 100)
        },

        watch: {
            value(newVal) {
                if (this.$refs.editable && 
                    this.$refs.editable.innerText !== newVal &&
                    this.getTextFromElement(this.$refs.editable) !== newVal) {
                    this.setContent(newVal)
                }
            }
        },

        methods: {
            focus() {
                if (this.$refs.editable) {
                    this.$refs.editable.focus()
                    // Ставим курсор в конец
                    const range = document.createRange()
                    const sel = window.getSelection()
                    range.selectNodeContents(this.$refs.editable)
                    range.collapse(false)
                    sel.removeAllRanges()
                    sel.addRange(range)
                }
            },
            getTextFromElement(el) {
                let text = ''
                for (const node of el.childNodes) {
                    if (node.nodeType === Node.TEXT_NODE) {
                        text += node.textContent
                    } else if (node.nodeType === Node.ELEMENT_NODE) {
                        if (node.tagName === 'DIV' || node.tagName === 'P') {
                            text += node.textContent + '\n'
                        } else if (node.tagName === 'BR') {
                            text += '\n'
                        } else {
                            text += node.textContent
                        }
                    }
                }
                return text
            },
            
            setContent(text) {
                if (this.$refs.editable) {
                    this.$refs.editable.textContent = text || ''
                }
            },

            // Обработка КОПИРОВАНИЯ - чистим текст
            handleCopy(event) {
                // Останавливаем стандартное поведение
                event.preventDefault()

                // Получаем выделенный текст
                const selection = window.getSelection()
                let text = selection.toString()

                // ОЧИЩАЕМ текст для буфера
                text = this.cleanClipboardText(text)

                // Записываем ОЧИЩЕННЫЙ текст в буфер
                if (event.clipboardData) {
                    event.clipboardData.setData('text/plain', text)

                    // Если нужно можно добавить HTML версию
                    // const html = this.textToHtml(text)
                    // event.clipboardData.setData('text/html', html)
                } else if (window.clipboardData) {
                    // Для старых IE
                    window.clipboardData.setData('Text', text)
                }

                // console.log('Скопировано (очищено):', text)
            },

            // Обработка ВСТАВКИ - чистим вставляемый текст
            handlePaste(event) {
                event.preventDefault()

                const clipboardData = event.clipboardData || window.clipboardData
                let pastedText = clipboardData.getData('text/plain')

                if (!pastedText) {
                    pastedText = clipboardData.getData('text')
                }

                // Очищаем текст
                pastedText = this.cleanClipboardText(pastedText)

                // ВАЖНО: Заменяем переносы строк на <br> или div для contenteditable
                pastedText = this.convertNewlinesToHtml(pastedText)

                // Вставляем как HTML
                this.insertHtmlAtCursor(pastedText)

                this.$nextTick(() => {
                    this.onInput({ target: this.$refs.editable })
                })
            },

            // Конвертируем \n в HTML разрывы
            convertNewlinesToHtml(text) {
                // Вариант 1: Просто заменяем \n на <br>
                // return text.replace(/\n/g, '<br>')

                // Вариант 2: Оборачиваем каждую строку в div (лучше для contenteditable)
                const lines = text.split('\n')
                return lines.map(line => `<div>${this.escapeHtml(line)}</div>`).join('')
            },

            // Вставка HTML вместо текста
            insertHtmlAtCursor(html) {
                const el = this.$refs.editable
                el.focus()

                if (document.execCommand && document.execCommand('insertHTML', false, html)) {
                    return
                }

                // Fallback
                const selection = window.getSelection()
                if (selection.rangeCount > 0) {
                    const range = selection.getRangeAt(0)
                    range.deleteContents()

                    const tempDiv = document.createElement('div')
                    tempDiv.innerHTML = html

                    const fragment = document.createDocumentFragment()
                    while (tempDiv.firstChild) {
                        fragment.appendChild(tempDiv.firstChild)
                    }

                    range.insertNode(fragment)
                    range.setStartAfter(fragment.lastChild)
                    range.setEndAfter(fragment.lastChild)
                    selection.removeAllRanges()
                    selection.addRange(range)
                } else {
                    // Если нет выделения, добавляем в конец
                    el.innerHTML += html
                }
            },

            // Также нужно обновить cleanClipboardText для сохранения переносов:
            cleanClipboardText(text) {
                if (!text) return ''

                // 1. Убираем HTML теги если есть
                text = text.replace(/<[^>]*>/g, '')

                // 2. Заменяем HTML entities
                const entities = {
                    '&nbsp;': ' ',
                    '&amp;': '&',
                    '&lt;': '<',
                    '&gt;': '>',
                    '&quot;': '"',
                    '&#39;': "'",
                }

                for (const [entity, replacement] of Object.entries(entities)) {
                    text = text.replace(new RegExp(entity, 'g'), replacement)
                }

                // 3. Убираем невидимые символы
                // Zero-width spaces и другие
                text = text.replace(/[\u200B-\u200D\uFEFF\u00A0]/g, ' ')

                // 4. Нормализуем пробелы и табуляции
                // Заменяем последовательности пробелов на один пробел
                text = text.replace(/[ ]+/g, ' ')
                // text = text.replace(/[ \t]+/g, ' ')

                // 5. Нормализуем переносы строк
                text = text.replace(/\r\n/g, '\n') // Windows
                text = text.replace(/\r/g, '\n') // Mac
                
                // 6. Убираем лишние пробелы в начале/конце строк
                // text = text.split('\n')
                //     .map(line => line.trim())
                //     .join('\n')
                
                // // 7. Убираем пустые строки в начале и конце
                // text = text.trim()

                // Возвращаем с переносами
                return text
            },

            // И обновить onInput чтобы правильно обрабатывать HTML:
            onInput(event) {
                this.$emit('input', event.target.innerText)
            },

            // Обработка ВЫРЕЗАНИЯ
            handleCut(event) {
                // Сначала обрабатываем как копирование
                this.handleCopy(event)

                // Затем удаляем выделенный текст
                document.execCommand('delete')

                // Триггерим input событие
                this.$nextTick(() => {
                    this.onInput({ target: this.$refs.editable })
                })
            },

            // Конвертация текста в простой HTML (для совместимости)
            textToHtml(text) {
                // Простейший HTML - только div с переносами
                return text
                    .split('\n')
                    .map(line => `<div>${this.escapeHtml(line)}</div>`)
                    .join('')
            },

            // Экранирование HTML
            escapeHtml(text) {
                const div = document.createElement('div')
                div.textContent = text
                return div.innerHTML
            },

            // Вставка текста на позицию курсора
            insertTextAtCursor(text) {
                const el = this.$refs.editable
                el.focus()

                if (document.execCommand && document.execCommand('insertText', false, text)) {
                    return
                }

                // Fallback для браузеров без execCommand
                const selection = window.getSelection()
                if (selection.rangeCount > 0) {
                    const range = selection.getRangeAt(0)
                    range.deleteContents()
                    const textNode = document.createTextNode(text)
                    range.insertNode(textNode)
                    range.setStartAfter(textNode)
                    range.setEndAfter(textNode)
                    selection.removeAllRanges()
                    selection.addRange(range)
                } else {
                    // Если нет выделения, добавляем в конец
                    el.textContent += text
                }
            },

            moveCursorToEnd() {
                const el = this.$refs.editable
                if (!el) return

                el.focus()
                const range = document.createRange()
                const sel = window.getSelection()
                range.selectNodeContents(el)
                range.collapse(false)
                sel.removeAllRanges()
                sel.addRange(range)
            },

            // Дополнительный метод для отладки
            debugClipboard() {
                navigator.clipboard
                    .readText()
                    .then(text => {
                        console.log('Текущий буфер:', text)
                        console.log('Длина:', text.length)
                        console.log('Коды символов:')
                        for (let i = 0; i < text.length; i++) {
                            console.log(`  ${i}: '${text[i]}' (0x${text.charCodeAt(i).toString(16)})`)
                        }
                    })
                    .catch(err => console.error('Не удалось прочитать буфер:', err))
            },
        },
    }
</script>

<style scoped>
    .custom-textarea {
        border: 1px solid #ddd;
        border-radius: 2px;
        padding: 10px;
        min-height: 300px;
        max-height: 300px;
        overflow-y: auto;
        font-family: monospace; /* Моноширинный для табличных данных */
        font-size: 15px;
        line-height: 1.5;
        white-space: pre-wrap; /* Сохраняет пробелы и переносы */
        word-break: break-all; /* Перенос длинных строк */
    }

    .custom-textarea:focus {
        border-color: #1976d2;
        outline: none;
    }
</style>
